import {
  Avatar,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  Group,
  Image,
  MultiSelect,
  Radio,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Footer from '../../components/Footer';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import { Page } from '../../constants/pageTitles';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import mainLogo from '../../logo.png';

const UNNAMED_APP = 'An unnamed app';

const ConnectionProblem = ({ message }) => (
  <Stack gap="xs">
    <ErrorMessage message={message} />
    <Text size="sm" c="dimmed">
      You can close this page and start the connection again from your AI assistant.
    </Text>
  </Stack>
);

const OAuthConsentPage = () => {
  const fetch = useFetch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const rawRequestId = queryString.parse(location.search).request;
  const requestId =
    typeof rawRequestId === 'string' && /^[A-Za-z0-9_-]+$/.test(rawRequestId) ? rawRequestId : undefined;
  const accounts = [];
  for (const { account } of user.userRoles) {
    if (account && !accounts.some(({ id }) => id === account.id)) {
      accounts.push(account);
    }
  }

  const [request, setRequest] = useState();
  const [loadError, setLoadError] = useState();
  const [accountId, setAccountId] = useState(accounts.length === 1 ? accounts[0].id : null);
  const [storeChoice, setStoreChoice] = useState('all');
  const [domainIds, setDomainIds] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [pendingAction, setPendingAction] = useState();
  const [submitError, setSubmitError] = useState();

  const requestUrl = `/api/admin/oauth/requests/${encodeURIComponent(requestId)}`;
  const account = accounts.find((a) => a.id === accountId);
  const canAllow = account && scopes.length > 0 && (storeChoice === 'all' || domainIds.length > 0);

  useEffect(() => {
    if (!requestId) {
      return;
    }
    fetch(requestUrl)
      .then(({ request }) => {
        setRequest(request);
        setScopes(request.scopes.map((scope) => scope.name));
      })
      .catch(setLoadError);
  }, [fetch, requestId, requestUrl]);

  const changeAccount = (value) => {
    setAccountId(value);
    setDomainIds([]);
  };

  const respond = (action, body) => {
    setPendingAction(action);
    setSubmitError(undefined);
    fetch(`${requestUrl}/${action}`, { method: 'POST', body: JSON.stringify(body), noLoginRedirect: true })
      .then(({ redirectUrl }) => {
        if (!redirectUrl) {
          throw new Error('Unexpected response from the server.');
        }
        window.location.assign(redirectUrl);
      })
      .catch((err) => {
        setSubmitError(getErrorMessage(err));
        setPendingAction(undefined);
      });
  };

  const allow = () =>
    respond('consent', {
      accountId,
      scopes,
      ...(storeChoice === 'selected' && { domainIds }),
    });

  let content;
  if (!requestId) {
    content = <ConnectionProblem message="This connection link is incomplete." />;
  } else if (loadError) {
    content = <ConnectionProblem message={getErrorMessage(loadError)} />;
  } else if (!request) {
    content = <Spinner />;
  } else {
    const appName = request.client.name || UNNAMED_APP;
    content = (
      <Stack>
        <Box>
          <Group wrap="nowrap" mb="xs">
            {request.client.logoUri && <Avatar src={request.client.logoUri} alt={appName} size="lg" radius="md" />}
            <Title order={3}>{appName} wants to access your Releva account</Title>
          </Group>
          <Text size="sm" c="dimmed">
            You will be sent back to {request.client.redirectHost}
          </Text>
        </Box>

        <Select
          label="Account"
          placeholder="Choose an account"
          data={accounts.map((a) => ({ value: a.id, label: a.name }))}
          value={accountId}
          onChange={changeAccount}
          allowDeselect={false}
          searchable
          nothingFoundMessage="No account matches"
        />

        <Radio.Group label="Stores" value={storeChoice} onChange={setStoreChoice}>
          <Stack gap="xs" mt="xs">
            <Radio value="all" label="All stores" />
            <Radio value="selected" label="Only selected stores" />
          </Stack>
        </Radio.Group>
        {storeChoice === 'selected' && (
          <MultiSelect
            aria-label="Selected stores"
            placeholder="Choose stores"
            data={account ? account.domains.map((d) => ({ value: d.id, label: d.name })) : []}
            value={domainIds}
            onChange={setDomainIds}
            disabled={!account}
            searchable
            nothingFoundMessage="No store matches"
          />
        )}

        <Checkbox.Group
          label="Permissions"
          description="Untick anything you do not want to allow."
          value={scopes}
          onChange={setScopes}
        >
          <Stack gap="xs" mt="xs">
            {request.scopes.map((scope) => (
              <Checkbox key={scope.name} value={scope.name} label={scope.description || scope.name} />
            ))}
          </Stack>
        </Checkbox.Group>

        <Text size="sm">
          Releva never sends a campaign, applies a flow change or deletes anything on the assistant's behalf. You can
          disconnect at any time in {'Settings > Account > Connected apps'}.
        </Text>

        <ErrorMessage message={submitError} />

        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => respond('deny')}
            loading={pendingAction === 'deny'}
            disabled={pendingAction === 'consent'}
          >
            Cancel
          </Button>
          <Button
            onClick={allow}
            loading={pendingAction === 'consent'}
            disabled={pendingAction === 'deny' || !canAllow}
          >
            Allow
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Box>
      <Metadata title={Page['oauthConsent']} />
      <Center pt={60}>
        <Container size="xs" w="100%">
          <Card withBorder p="lg">
            <Image src={mainLogo} alt="Releva" w="auto" maw="100%" mx="auto" mb="lg" />
            {content}
          </Card>
          <Footer />
        </Container>
      </Center>
    </Box>
  );
};

export default OAuthConsentPage;
